/**
 * Get CSRF token from meta tag
 * This reads the token that Yii2 keeps updated in the HTML head
 */
function getCsrfToken(): string | null {
  // Only access DOM when this function is called, not at module load time
  if (typeof document === 'undefined') {
    return null;
  }
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag ? metaTag.getAttribute('content') : null;
}

/**
 * Wrapper around native fetch that automatically includes CSRF token
 */
export async function fetchWithCsrf(url: string, options: RequestInit = {}): Promise<Response> {
  const csrfToken = getCsrfToken();
  
  // Clone headers to avoid mutating the original
  const headers = new Headers(options.headers || {});
  
  // Add CSRF token for state-changing methods
  if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method?.toUpperCase() || '')) {
    headers.set('X-CSRF-Token', csrfToken);
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers
    });

    // Handle CSRF token validation failure
    if (response && response.status === 400) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          const data = await response.json();
          if (data.message?.includes('CSRF')) {
            console.error('CSRF token validation failed. Reloading page...');
            window.location.reload();
          }
        } catch (e) {
          // If parsing fails, just continue
        }
      }
    }

    return response;
  } catch (error) {
    // Re-throw the error to be handled by the caller
    throw error;
  }
}

/**
 * Convenience methods for common HTTP operations
 */
export const api = {
  get: (url: string, options?: RequestInit) => 
    fetchWithCsrf(url, { ...options, method: 'GET' }),
    
  post: (url: string, body?: any, options?: RequestInit) => 
    fetchWithCsrf(url, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: {
        ...options?.headers,
        ...(!(body instanceof FormData) && { 'Content-Type': 'application/json' })
      }
    }),
    
  put: (url: string, body?: any, options?: RequestInit) => 
    fetchWithCsrf(url, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
      headers: {
        ...options?.headers,
        ...(!(body instanceof FormData) && { 'Content-Type': 'application/json' })
      }
    }),
    
  delete: (url: string, options?: RequestInit) => 
    fetchWithCsrf(url, { ...options, method: 'DELETE' })
};

/**
 * Legacy exports for backward compatibility
 * @deprecated Use named exports instead
 */
export default fetchWithCsrf;